import FetchClient from "./FetchClient";

export interface StripePrice {
  id: string;
  amount: number;
  currency: string;
  interval: string;
  metadata: Record<string, string>;
  isTrialEligible: boolean;
}

export interface StripeProduct {
  id: string;
  name: string;
  prices: StripePrice[];
}

export interface ProductsResponse {
  discordAccess: StripeProduct;
  retailerAccess: Record<string, StripeProduct>;
}

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  price: number;
  priceId: string;
  frequency: "monthly" | "yearly" | "one-time";
  features: string[];
  storeLimit: number;
  trackedProducts?: number;
  popular?: boolean;
  retailer?: string;
  isTrialEligible?: boolean;
}

export interface BillingConfig {
  [retailer: string]: Plan[];
}

// Fetch products and prices from the server
export const getProductsAndPrices = async (): Promise<ProductsResponse> => {
  try {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now();
    // Pricing catalog is a public endpoint (server registers /v1/products-and-prices
    // without auth) and the landing page needs it while logged out, so request it
    // without credentials — a required-auth call throws "session expired" whenever
    // tokens are absent.
    const response = await FetchClient.get<ProductsResponse>(
      `/v1/products-and-prices?t=${timestamp}`,
      {},
      true
    );

    if (!response || !response.retailerAccess) {
      console.error("❌ Invalid API response:", response);
      throw new Error("Invalid API response");
    }

    return response;
  } catch (error) {
    console.error("❌ Failed to fetch products and prices:", error);
    throw error;
  }
};

// Convert Stripe data to our billing config format
export const convertStripeDataToBillingConfig = (
  data: ProductsResponse
): BillingConfig => {
  const config: BillingConfig = {};

  // Check if data is valid
  if (!data || !data.retailerAccess) {
    console.error(
      "Invalid data provided to convertStripeDataToBillingConfig:",
      data
    );
    return config;
  }

  // Process each retailer
  Object.entries(data.retailerAccess).forEach(([retailer, product]) => {
    const plans: Plan[] = product.prices.map((price) => {
      // Extract plan name from plan metadata (e.g., "walmart-basic" -> "basic")
      const fullPlanName = price.metadata.plan || "";
      const plan = fullPlanName.includes("_")
        ? fullPlanName.split("_").slice(1).join("_")
        : "basic";
      const stores = parseInt(price.metadata.stores || "1");

      // Generate features based on plan and retailer
      const features = generateFeatures(retailer, plan, stores);

      return {
        id: `${retailer}-${plan}`,
        name: `${stores} Store${stores > 1 ? "s" : ""} (${
          plan.charAt(0).toUpperCase() + plan.slice(1)
        })`,
        displayName: price.metadata.name || "",
        price: price.amount / 100, // Convert from cents
        priceId: price.id,
        frequency: price.interval === "month" ? "monthly" : "yearly",
        features,
        storeLimit: stores,
        trackedProducts: calculateTrackedProducts(stores, plan),
        popular: plan === "pro",
        retailer,
        isTrialEligible: price.isTrialEligible,
      };
    });

    config[retailer] = plans;
  });

  return config;
};

// Generate features based on retailer, plan, and store count
const generateFeatures = (
  retailer: string,
  plan: string,
  stores: number
): string[] => {
  const features = ["discordAccess"];

  // Add retailer-specific access
  if (retailer === "walmart") {
    features.push("walmartAccess");
  } else if (retailer === "home_depot") {
    features.push("homeDepotAccess");
  }

  // Add store count feature
  if (stores === 1) {
    features.push("1Store");
  } else if (stores <= 5) {
    features.push("5Stores");
  } else if (stores <= 7) {
    features.push("7Stores");
  } else {
    features.push("10Stores");
  }

  // Add plan-specific features
  features.push("allCategories");

  if (plan === "pro" || plan === "enterprise") {
    features.push("advancedMonitoring");
    features.push("prioritySupport");
  }

  if (plan === "enterprise") {
    features.push("premiumMonitoring");
    features.push("dedicatedSupport");
  }

  return features;
};

// Calculate tracked products based on store count and plan
const calculateTrackedProducts = (stores: number, plan: string): number => {
  let base = stores * 5; // 5 products per store base

  if (plan === "pro") {
    base *= 1.5;
  } else if (plan === "enterprise") {
    base *= 2;
  }

  return Math.round(base);
};

// Get plans for a specific retailer
export const getPlansForRetailer = async (
  retailer: string
): Promise<Plan[]> => {
  const data = await getProductsAndPrices();
  const config = convertStripeDataToBillingConfig(data);
  return config[retailer] || [];
};

// Get all available retailers
export const getAvailableRetailers = async (): Promise<string[]> => {
  const data = await getProductsAndPrices();
  return Object.keys(data.retailerAccess);
};

// Get Discord access price
export const getDiscordAccessPrice = async (): Promise<StripePrice | null> => {
  const data = await getProductsAndPrices();
  return data.discordAccess.prices[0] || null;
};

// Validate price selection (Discord + one per retailer)
export const validatePriceSelection = (
  priceIds: string[],
  data: ProductsResponse
): { valid: boolean; error?: string } => {
  const retailerCount: Record<string, number> = {};

  priceIds.forEach((priceId) => {
    Object.entries(data.retailerAccess).forEach(([retailer, product]) => {
      if (product.prices.some((price) => price.id === priceId)) {
        retailerCount[retailer] = (retailerCount[retailer] || 0) + 1;
      }
    });
  });

  const multipleRetailers = Object.entries(retailerCount).filter(
    ([_, count]) => count > 1
  );
  if (multipleRetailers.length > 0) {
    return {
      valid: false,
      error: `Only one plan per retailer allowed. Multiple plans selected for: ${multipleRetailers
        .map(([retailer]) => retailer)
        .join(", ")}`,
    };
  }

  return { valid: true };
};
